import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const C = {
  bg:      '#f8fafc',
  surface: '#ffffff',
  border:  '#e5e7eb',
  blue:    '#3b82f6',
  green:   '#16a34a',
  amber:   '#f59e0b',
  red:     '#ef4444',
  text:    '#0f172a',
  textSec: '#374151',
  textMut: '#64748b',
};

const MAX_CHARS = 5300;
const AMBER_AT  = 4500;

export default function PersonalStatement({ session }) {
  const isMobile = useIsMobile();
  const [content, setContent]           = useState('');
  const [status, setStatus]             = useState('in_progress');
  const [loading, setLoading]           = useState(true);
  const [saveState, setSaveState]         = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [infoCollapsed, setInfoCollapsed] = useState(true);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [loadError, setLoadError]         = useState(false);
  const debounceRef = useRef(null);
  const uid = session?.user?.id;

  useEffect(function() {
    if (!uid) { setLoading(false); return; }

    try {
      const key = 'previsr_ps_info_collapsed_' + uid;
      if (!localStorage.getItem(key)) {
        setInfoCollapsed(false);
        localStorage.setItem(key, '1');
      }
    } catch (_) {}

    supabase.from('personal_statements')
      .select('content, status')
      .eq('user_id', uid)
      .then(function({ data, error }) {
        if (error) {
          setLoadError(true);
        } else if (data && data.length > 0) {
          setContent(data[0].content || '');
          setStatus(data[0].status || 'in_progress');
        }
        setLoading(false);
      });

    return function() { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [uid]);

  function handleChange(e) {
    const val = e.target.value;
    setContent(val);
    setSaveState('saving');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function() {
      persist(val, status);
    }, 1000);
  }

  async function persist(text, currentStatus) {
    if (!uid) return;
    const { error } = await supabase.from('personal_statements').upsert(
      { user_id: uid, content: text, status: currentStatus, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );
    if (error) {
      setSaveState('error');
      setTimeout(function() { setSaveState('idle'); }, 3000);
    } else {
      setSaveState('saved');
      setTimeout(function() { setSaveState('idle'); }, 2000);
    }
  }

  async function updateSprintTasks(newStatus) {
    if (!uid) return;
    const rows = newStatus === 'final' ? [
      { user_id: uid, item_id: 's2_ps',      status: 'done',       done: true  },
      { user_id: uid, item_id: 's3_ps_final', status: 'done',       done: true  },
    ] : [
      { user_id: uid, item_id: 's2_ps',      status: 'inprogress', done: false },
      { user_id: uid, item_id: 's3_ps_final', status: 'todo',       done: false },
    ];
    const { error } = await supabase.from('pre_app_checklist').upsert(rows, { onConflict: 'user_id,item_id' });
    return !error;
  }

  function handleStatusClick(newStatus) {
    if (newStatus === status) return;
    if (newStatus === 'final') {
      setShowConfirm(true);
    } else {
      setStatus('in_progress');
      persist(content, 'in_progress');
      updateSprintTasks('in_progress');
    }
  }

  async function confirmFinal() {
    setShowConfirm(false);
    setStatus('final');
    await persist(content, 'final');
    await updateSprintTasks('final');
  }

  const charCount    = content.length;
  const counterColor = charCount > MAX_CHARS ? C.red : charCount > AMBER_AT ? C.amber : C.textMut;

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ width: 26, height: 26, border: `3px solid ${C.border}`, borderTopColor: C.blue, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
    </div>
  );

  if (loadError) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ fontSize: 14, color: C.textSec }}>Could not load your personal statement. Please refresh and try again.</div>
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg, minHeight: '100vh' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Confirm modal */}
      {showConfirm && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }} onClick={function() { setShowConfirm(false); }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 201, background: C.surface, borderRadius: 14, padding: '28px 32px', width: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 10 }}>Mark as final version?</div>
            <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.65, marginBottom: 24 }}>
              This will check off the personal statement tasks in your sprint checklist. You can switch back to "In Progress" at any time.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={function() { setShowConfirm(false); }}
                style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.textSec, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmFinal}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: C.green, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Confirm
              </button>
            </div>
          </div>
        </>
      )}

      {/* Topbar */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '14px 16px' : '14px 24px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: '-0.3px' }}>Personal Statement</div>
        <div style={{ fontSize: 11, color: C.textMut, marginTop: 2 }}>Your primary application essay — 5,300 characters max</div>
      </div>

      <div style={{ padding: isMobile ? '16px' : '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 860, width: '100%' }}>

        {/* Info section */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <button
            onClick={function() { setInfoCollapsed(function(v) { return !v; }); }}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>What is the personal statement?</span>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke={C.textMut} strokeWidth="2.5"
              style={{ transform: infoCollapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform .2s', flexShrink: 0 }}>
              <polyline points="6,9 12,15 18,9"/>
            </svg>
          </button>
          {!infoCollapsed && (
            <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
              <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.8, margin: '0 0 14px' }}>
                The personal statement is the one essay every medical school sees through AMCAS. It is your opportunity to explain why you want to become a physician — not a list of accomplishments, but a coherent narrative that connects your experiences to your decision.
              </p>
              <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.8, margin: '0 0 14px' }}>
                Schools require it because numbers do not reveal much about a person. Admissions committees want to understand what has shaped your perspective, what drives you toward medicine beyond grades and scores, and how your path has prepared you for this work.
              </p>
              <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.8, margin: '0 0 14px' }}>
                Plan to spend 4 to 8 weeks on this — multiple drafts, feedback from mentors or advisors who know your story, and real revision time. Submitting AMCAS early matters. Do not let a slow personal statement be the reason you miss the rolling admissions window.
              </p>
              <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.8, margin: 0 }}>
                Committees read thousands of these essays. Generic statements about wanting to help people do not stand out. Be specific: name the patient encounter, the conversation, the moment that changed how you think about medicine. Specificity is what makes an essay memorable.
              </p>
            </div>
          )}
        </div>

        {/* Editor card */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>

          {/* Status toggle */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: C.textMut, marginRight: 4 }}>Status</span>
            <button
              onClick={function() { handleStatusClick('in_progress'); }}
              style={{ padding: '5px 16px', borderRadius: 20, border: `1.5px solid ${status === 'in_progress' ? C.blue : C.border}`, background: status === 'in_progress' ? '#eff6ff' : 'transparent', color: status === 'in_progress' ? C.blue : C.textMut, fontSize: 12, fontWeight: status === 'in_progress' ? 600 : 400, cursor: 'pointer', transition: 'all .15s' }}>
              In Progress
            </button>
            <button
              onClick={function() { handleStatusClick('final'); }}
              style={{ padding: '5px 16px', borderRadius: 20, border: `1.5px solid ${status === 'final' ? C.green : C.border}`, background: status === 'final' ? C.green : 'transparent', color: status === 'final' ? '#fff' : C.textMut, fontSize: 12, fontWeight: status === 'final' ? 600 : 400, cursor: 'pointer', transition: 'all .15s' }}>
              Final Version
            </button>
            {status === 'final' && (
              <span style={{ fontSize: 11, color: C.green, marginLeft: 4 }}>Sprint tasks marked complete</span>
            )}
          </div>

          {/* Textarea */}
          <div style={{ padding: '16px' }}>
            <textarea
              value={content}
              onChange={handleChange}
              placeholder="Start writing your personal statement here..."
              style={{ width: '100%', minHeight: 400, fontSize: 14, lineHeight: 1.8, color: C.text, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '16px', resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', display: 'block' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 11, color: saveState === 'saving' ? C.textMut : saveState === 'error' ? C.red : C.green, visibility: saveState === 'idle' ? 'hidden' : 'visible' }}>
                {saveState === 'saving' ? 'Saving...' : saveState === 'error' ? 'Save failed' : 'Saved'}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: counterColor }}>
                {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
              </span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
